import { Modal } from "antd";
import { SearchQuery } from "../../code/search-query/search-query";
import {
  incidentsViewColumns,
  mapIncientsViewQuery,
  incidentsSearchSchema,
} from "./IncidentsView.default";
import {
  getIncidents,
  getIncidentReporter,
} from "../../repository/data.repository";
import { PaginationInput } from "../../repository/data.default";
import { createGridRequest } from "../../hooks/useGridRequest";
import ManagedGridView from "../../controls/ManagedGridView";
import { DataAction } from "../../controls/Data.default";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";
import { getProblemMessage } from "../../utils/http";

export interface IncidentsViewProps {
  readonly trigger?: string;
  readonly onAction?: (key: string, record: any) => void;
  readonly onSearch?: (searchQuery: SearchQuery) => void;
}

function IncidentsView(props: IncidentsViewProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  const isAdmin = useSelector(authorizeSelectors.isAppAdmin);
  const [modal, contextHolder] = Modal.useModal();

  // The API masks the reporter on anonymous incidents (reportedByUserUid → null). Surface a literal
  // "Anonymous" in the grid so the cell isn't blank; admins can still reveal via the row action.
  const request = createGridRequest(
    (input: PaginationInput) =>
      getIncidents(input).then((result) => ({
        ...result,
        data: (result.data ?? []).map((incident) =>
          incident.isAnonymous || !incident.reportedByUserUid
            ? { ...incident, reportedByUserUserName: "Anonymous" }
            : incident,
        ),
      })),
    { retry: true },
  );

  function onRevealReporter(record: any): void {
    getIncidentReporter(record.uid)
      .then((reporter) => {
        modal.info({
          title: "Reporter",
          centered: true,
          content:
            reporter?.reportedByUserUserName ??
            reporter?.reportedByUserUid ??
            "Reporter not available",
        });
      })
      .catch((e: any) => {
        modal.error({
          title: "Reporter",
          centered: true,
          content: getProblemMessage(e) || "Failed to reveal reporter",
        });
      });
  }

  return (
    <>
      <ManagedGridView
        trigger={props.trigger}
        columns={incidentsViewColumns}
        schema={incidentsSearchSchema}
        mapQuery={mapIncientsViewQuery}
        onSearch={props.onSearch}
        request={request}
        pageSize={100}
        actions={(record: any): DataAction[] => [
          // Special action first (per the row-action convention: special → Edit → danger last).
          // Admins can unmask the reporter behind an anonymous incident.
          ...(isAdmin && record?.isAnonymous
            ? [
                {
                  key: "reveal",
                  label: "Reveal reporter",
                  handler: (_key: string, rec: any) => onRevealReporter(rec),
                },
              ]
            : []),
          {
            key: "update",
            label: "Edit",
            disabled: !permissions.can("Incidents", "edit"),
            handler: (key: string, rec: any) => props.onAction?.(key, rec),
          },
          {
            key: "delete",
            label: "Delete",
            disabled: !permissions.can("Incidents", "delete"),
            danger: true,
            handler: (key: string, rec: any) => props.onAction?.(key, rec),
          },
        ]}
      />
      {contextHolder}
    </>
  );
}

export default IncidentsView;
